import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit3, X, Save } from "lucide-react";
import SeriesEditor from "../components/SeriesEditor";
import AnalyticsView from "../components/AnalyticsView";
import FileUpload from "../components/FileUpload";
import { mediaUrl } from "../lib/media";

const TABS = [
    { key: "series", label: "Series" },
    { key: "viral", label: "Viral Videos" },
    { key: "ai", label: "AI Creative" },
    { key: "coming", label: "Coming Soon" },
    { key: "analytics", label: "Analytics" },
];

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const nav = useNavigate();
    const [tab, setTab] = useState("series");
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({});
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        if (!loading && !user) nav("/admin", { replace: true });
    }, [loading, user, nav]);

    const load = async () => {
        if (tab === "viral") {
            const r = await api.get("/videos/viral");
            setItems(r.data);
        } else if (tab === "ai") {
            const r = await api.get("/ai-creative");
            setItems(r.data);
        } else if (tab === "coming") {
            const r = await api.get("/coming-soon");
            setItems(r.data);
        }
    };

    useEffect(() => {
        if (user && tab !== "series" && tab !== "analytics") load();
        setForm({});
        setEditingId(null); /* reset on tab change */
    }, [tab, user]);

    const buildPayload = () => {
        if (tab === "viral") {
            return {
                title: form.title,
                thumbnail: form.thumbnail,
                video_url: form.video_url || null,
                duration: form.duration || "2:00",
                views: form.views || "0",
                likes: form.likes || "0",
                category: form.category || "Comedy",
                trending: !!form.trending,
                description: form.description || "",
            };
        }
        if (tab === "ai") {
            return {
                title: form.title,
                thumbnail: form.thumbnail,
                category: form.category || "AI Ads",
                video_url: form.video_url || null,
                duration: form.duration || "1:00",
                description: form.description || "",
            };
        }
        // coming
        return {
            title: form.title,
            subtitle: form.subtitle || "",
            poster: form.poster || form.thumbnail,
            release_date: form.release_date,
            description: form.description || "",
        };
    };

    const submit = async (e) => {
        e.preventDefault();
        const path = tab === "viral" ? "viral" : tab === "ai" ? "ai-creative" : "coming-soon";
        try {
            if (editingId) {
                await api.put(`/admin/${path}/${editingId}`, buildPayload());
                toast.success("Updated.");
            } else {
                await api.post(`/admin/${path}`, buildPayload());
                toast.success("Created.");
            }
            setForm({});
            setEditingId(null);
            load();
        } catch (err) {
            toast.error(editingId ? "Update failed." : "Failed to create.");
        }
    };

    const startEdit = (item) => {
        setEditingId(item.id);
        setForm({
            title: item.title || "",
            thumbnail: item.thumbnail || "",
            poster: item.poster || "",
            subtitle: item.subtitle || "",
            video_url: item.video_url || "",
            duration: item.duration || "",
            category: item.category || "",
            views: item.views || "",
            likes: item.likes || "",
            trending: !!item.trending,
            release_date: item.release_date || "",
            description: item.description || "",
        });
        // Scroll form into view on smaller screens
        if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setForm({});
    };

    const del = async (id) => {
        if (!confirm("Delete this item?")) return;
        const path = tab === "viral" ? "viral" : tab === "ai" ? "ai-creative" : "coming-soon";
        try {
            await api.delete(`/admin/${path}/${id}`);
            toast.success("Deleted.");
            load();
        } catch { toast.error("Delete failed."); }
    };

    if (loading || !user) return <div className="pt-32 mx-auto max-w-[1200px] px-6 text-white/60">Checking session…</div>;

    return (
        <div data-testid="admin-dashboard-page" className="pt-28 pb-24 mx-auto max-w-[1400px] px-6 lg:px-10">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#FFD400]">Admin</span>
                    <h1 className="font-display font-black text-4xl tracking-tight">Content Dashboard</h1>
                    <p className="text-white/50 text-sm mt-1">Signed in as {user.email}</p>
                </div>
                <button
                    data-testid="admin-logout-btn"
                    onClick={async () => { await logout(); nav("/admin"); }}
                    className="inline-flex items-center gap-2 text-sm bg-white/5 border border-white/10 rounded-full px-4 py-2 hover:bg-white/10 transition-colors"
                >
                    <LogOut className="w-4 h-4" /> Logout
                </button>
            </div>

            <div className="flex gap-2 mb-8">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        data-testid={`admin-tab-${t.key}`}
                        onClick={() => setTab(t.key)}
                        className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors ${
                            tab === t.key ? "bg-[#FFD400] text-black" : "bg-white/5 text-white/80 border border-white/10 hover:bg-white/10"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "series" ? (
                <SeriesEditor />
            ) : tab === "analytics" ? (
                <AnalyticsView />
            ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Create / Edit form */}
                <div className="rounded-[18px] border border-white/10 bg-white/[0.02] p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display font-bold text-xl" data-testid="form-heading">
                            {editingId ? `Edit ${TABS.find(t => t.key === tab).label}` : `Add ${TABS.find(t => t.key === tab).label}`}
                        </h2>
                        {editingId && (
                            <span className="text-[10px] uppercase tracking-widest text-[#FFD400] font-bold">Editing</span>
                        )}
                    </div>
                    <form onSubmit={submit} className="space-y-3">
                        <Field label="Title" value={form.title || ""} onChange={(v) => setForm({ ...form, title: v })} required testId="form-title" />
                        {tab !== "coming" && (
                            <FileUpload
                                label="Thumbnail"
                                kind="thumbnail"
                                accept="image/*"
                                value={form.thumbnail || ""}
                                onChange={(v) => setForm({ ...form, thumbnail: v })}
                                testId="form-thumbnail"
                            />
                        )}
                        {tab === "coming" && (
                            <>
                                <Field label="Subtitle" value={form.subtitle || ""} onChange={(v) => setForm({ ...form, subtitle: v })} testId="form-subtitle" />
                                <FileUpload label="Poster" kind="poster" accept="image/*" value={form.poster || ""} onChange={(v) => setForm({ ...form, poster: v })} testId="form-poster" />
                                <Field label="Release Date (ISO)" value={form.release_date || ""} onChange={(v) => setForm({ ...form, release_date: v })} required placeholder="2026-06-01T00:00:00Z" testId="form-release" />
                            </>
                        )}
                        {(tab === "viral" || tab === "ai") && (
                            <>
                                <FileUpload label="Video (MP4)" kind="video" accept="video/*" value={form.video_url || ""} onChange={(v) => setForm({ ...form, video_url: v })} testId="form-video" />
                                <Field label="Duration" value={form.duration || ""} onChange={(v) => setForm({ ...form, duration: v })} placeholder="2:30" testId="form-duration" />
                                <Field label="Category" value={form.category || ""} onChange={(v) => setForm({ ...form, category: v })} placeholder={tab === "ai" ? "AI Ads" : "Comedy"} testId="form-category" />
                            </>
                        )}
                        {tab === "viral" && (
                            <>
                                <Field label="Views" value={form.views || ""} onChange={(v) => setForm({ ...form, views: v })} placeholder="1.2M" testId="form-views" />
                                <label className="flex items-center gap-2 text-sm text-white/70">
                                    <input type="checkbox" checked={!!form.trending} onChange={(e) => setForm({ ...form, trending: e.target.checked })} data-testid="form-trending" />
                                    Mark as trending
                                </label>
                            </>
                        )}
                        <div>
                            <label className="text-xs uppercase tracking-widest text-white/50">Description</label>
                            <textarea
                                data-testid="form-description"
                                rows={3}
                                value={form.description || ""}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                className="mt-1 w-full bg-white/5 border border-white/10 rounded-[12px] px-3 py-2 text-white focus:border-[#FFD400] outline-none resize-none"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <button data-testid="form-submit" type="submit" className="inline-flex items-center gap-2 bg-[#FFD400] text-black font-bold rounded-full px-5 py-2.5 hover:scale-105 transition-transform">
                                {editingId ? <><Save className="w-4 h-4" /> Update</> : <><Plus className="w-4 h-4" /> Create</>}
                            </button>
                            {editingId && (
                                <button
                                    type="button"
                                    onClick={cancelEdit}
                                    data-testid="form-cancel"
                                    className="inline-flex items-center gap-2 text-sm bg-white/5 border border-white/10 text-white rounded-full px-4 py-2 hover:bg-white/10 transition-colors"
                                >
                                    <X className="w-4 h-4" /> Cancel
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                {/* List */}
                <div className="rounded-[18px] border border-white/10 bg-white/[0.02] p-6">
                    <h2 className="font-display font-bold text-xl mb-4">Existing ({items.length})</h2>
                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                        {items.map((it) => (
                            <div
                                key={it.id}
                                data-testid={`admin-item-${it.id}`}
                                className={`flex items-center gap-3 p-2 rounded-[12px] border transition-colors ${
                                    editingId === it.id
                                        ? "bg-[#FFD400]/[0.06] border-[#FFD400]/40"
                                        : "bg-white/[0.03] border-white/5"
                                }`}
                            >
                                <img src={mediaUrl(it.thumbnail || it.poster)} alt="" className="w-16 h-10 object-cover rounded-md" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold truncate">{it.title}</div>
                                    <div className="text-xs text-white/50 truncate">{it.category || it.subtitle}</div>
                                </div>
                                <button
                                    data-testid={`admin-edit-${it.id}`}
                                    onClick={() => startEdit(it)}
                                    aria-label="Edit"
                                    className="p-2 text-white/60 hover:text-[#FFD400] transition-colors"
                                >
                                    <Edit3 className="w-4 h-4" />
                                </button>
                                <button
                                    data-testid={`admin-delete-${it.id}`}
                                    onClick={() => del(it.id)}
                                    aria-label="Delete"
                                    className="p-2 text-white/60 hover:text-red-400 transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                        {items.length === 0 && <p className="text-sm text-white/50">Nothing yet.</p>}
                    </div>
                </div>
            </div>
            )}
        </div>
    );
}

function Field({ label, value, onChange, required, placeholder, testId }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-widest text-white/50">{label}</label>
            <input
                data-testid={testId}
                required={required}
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full bg-white/5 border border-white/10 rounded-[12px] px-3 py-2 text-white focus:border-[#FFD400] outline-none transition-colors"
            />
        </div>
    );
}
